import { FormControl, FormHelperText, FormLabel, RadioGroup as MuiRadioGroup, Radio } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import { type FC, type SyntheticEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import color from 'atoms/colors/colors';

import Flex from 'quarks/Flex';
import Text from 'quarks/Text';
import { BasicProps, basic } from 'quarks/interpolations/basic';
import { allCSSWithPseudos } from 'quarks/styleProps/all';

const StyledFormControl = styled(FormControl).withConfig({
  shouldForwardProp: prop => !Object.keys(allCSSWithPseudos).includes(prop),
})`
  && {
    ${basic}
  }
`;

interface RadioGroupProps extends BasicProps {
  /**
   * Text label of the Radio button
   */
  label?: string;
  /**
   * displays error message under the label (replaces helper text)
   */
  errorMessage?: string;
  /**
   * If `true`, the component is places in the error state
   */
  error?: boolean;
  /**
   * Determines the size of the `Radio`
   */
  size?: 'large' | 'small';
  /**
   * If `true`, the component is required.
   */
  required?: boolean;
  /**
   * displays text under the label to assist the user
   */
  helperText?: string;
  /**
   * Callback fired when the state is changed.
   * **Signature:**
   * `function(event: React.ChangeEvent<HTMLInputElement>) => void`
   * event: The event source of the callback. You can pull out the new value by accessing `event.target.value` (string). You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onChange?: (event: SyntheticEvent<Element, Event>, checked: boolean) => void;
  /**
   * Callback fired when losing focus.
   * **Signature:**
   * `function(event: React.ChangeEvent<HTMLInputElement>) => void`
   * event: The event source of the callback. You can pull out the new value by accessing `event.target.value` (string). You can pull out the new checked state by accessing `event.target.checked` (boolean).
   */
  onBlur?: (event: SyntheticEvent<Element, Event>, checked: boolean) => void;
  /**
   * The id of the `input` element.
   */
  id?: string;
  /**
   * The value of the component. The DOM API casts this to a string.
   */
  value?: string | boolean;
  /**
   * value for input's name attribute
   */
  name?: string;
  /**
   * initial default value
   */
  defaultValue?: string;
  /**
   * RadioGroup options as an array of objects.
   * `{ label: string, value: string }.
   */
  options?: [];
}

const RadioGroup: FC<RadioGroupProps> = ({
  name,
  label,
  size,
  required,
  value,
  defaultValue,
  helperText,
  id,
  options,
  error,
  errorMessage,
  onChange,
  ...props
}) => {
  const [currValue, setCurrValue] = useState(defaultValue || '');

  useEffect(() => {
    if (value) {
      setCurrValue(value);
    }
  }, [value]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const targetValue = event.target.value;
    setCurrValue(targetValue as string);
    onChange && onChange(event);
  };

  return (
    <StyledFormControl
      id={id}
      error={error}
      sx={{
        width: '100%',
        marginBottom: '16px',
      }}
      css={`
        label {
          color: ${color.gray[900]} !important;
        }
      `}
      {...props}
    >
      <Flex
        flexDirection="column"
        justifyContent="start"
        gap="5px"
        sm={{ flexDirection: 'row', justifyContent: 'space-between' }}
      >
        <FormLabel
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: color.gray[900],
          }}
        >
          {label}
          {required ? <Text textColor="sunset-400">*</Text> : ''}
        </FormLabel>
        {options.length > 0 && (
          <MuiRadioGroup
            name={name}
            value={currValue}
            onChange={handleChange}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              flexWrap: 'nowrap',
            }}
          >
            {options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option.value}
                control={
                  <Radio
                    size={size || 'small'}
                    sx={{ pointerEvents: 'none', marginTop: '0px' }}
                    css={`
                      &&.Mui-checked {
                        color: ${color.green[300]};
                      }
                    `}
                  />
                }
                label={option.label}
                sx={{
                  color: color.gray[900],
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
                css={`
                  .MuiTypography-root {
                    font-size: 14px;
                    font-weight: 500;
                  }
                `}
              />
            ))}
          </MuiRadioGroup>
        )}
      </Flex>
      {(helperText || error) && (
        <FormHelperText sx={{ fontSize: '12px' }}>{error ? errorMessage : helperText}</FormHelperText>
      )}
    </StyledFormControl>
  );
};

export default RadioGroup;
